import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router";
import "../../globals-duo.css";
import "@fontsource/source-sans-3/400.css";
import "@fontsource/source-sans-3/500.css";
import "@fontsource/source-sans-3/600.css";
import "@fontsource/source-sans-3/700.css";
import type { Question } from "../../components/exam/types";
import {
  useExamSession,
  type SessionMode,
} from "../../lib/exam/useExamSession";
import { useBreakpoint } from "../../lib/exam/useBreakpoint";
import { Composer } from "../../components/exam/Composer";
import { ContextualPill } from "../../components/exam/ContextualPill";
import { ExitConfirmDialog } from "../../components/exam/ExitConfirmDialog";
import { FeedbackBubble } from "../../components/exam/FeedbackBubble";
import { FeedbackBubbleSkeleton } from "../../components/exam/FeedbackBubbleSkeleton";
import { ExamTopBar } from "../../components/exam/ExamTopBar";
import { PartProgress } from "../../components/exam/PartProgress";
import { QuestionCard } from "../../components/exam/QuestionCard";
import { OverallTimer } from "../../components/exam/OverallTimer";
import { SettingsPopover } from "../../components/exam/SettingsPopover";
import { SluitenButton } from "../../components/exam/SluitenButton";
import { TimerPill } from "../../components/exam/TimerPill";
import { TimeUpOverlay } from "../../components/exam/TimeUpOverlay";
import { ExitConfirmDuo } from "../../components/exam/duo/ExitConfirmDuo";
import { StartDuoConfirm } from "../../components/exam/duo/StartDuoConfirm";
import { DuoHeader } from "../../components/exam/duo/DuoHeader";
import { DuoBottomBar } from "../../components/exam/duo/DuoBottomBar";
import { DuoQuestionCard } from "../../components/exam/duo/DuoQuestionCard";
import { DuoRecordPanel } from "../../components/exam/duo/DuoRecordPanel";
import { OverzichtModal } from "../../components/exam/duo/OverzichtModal";
import { TimeWarningModal } from "../../components/exam/duo/TimeWarningModal";
import type { TurnResponse } from "../../../../api/src/routes/exam";
import { authedFetch } from "../../lib/api-client";

const TURN_SECONDS = 60;
const FULL_EXAM_MINUTES = 35;

/** Server-rendered bootstrap data the LXC threaded into the Next.js
 *  RSC. In the SPA we accept it via React Router location.state from
 *  the home page's `/exam/start` response, OR fetch lazily by replaying
 *  the start call (skipped for now — sticking with state handoff plus a
 *  graceful "open from home" fallback for direct URL access). */
type ExamBootstrap = {
  sessionId: string;
  mode: SessionMode;
  parts: number[];
  totalTurns: number;
  partialMediaFailure: boolean;
  questions: Question[];
  /** Optional — present on GET /api/sessions/:id, omitted by /exam/start
   *  (which sticks with the user's preference resolved server-side). */
  skin?: "ember" | "duo";
  focusMode?: boolean;
};

type LocationState = {
  startResponse?: ExamBootstrap;
};

/**
 * Exam runner page — full Vite port of the LXC's
 * apps/a2-easy-web/app/exam/[sessionId]/{page,ExamRunnerShell,ExamRunnerEmber,
 * ExamRunnerDuo}.tsx into a single React Router route.
 *
 * The page reads server-rendered bootstrap data from location.state
 * (set by `/exam/start` on the home page). If state is missing (direct
 * URL access / refresh), it shows a "open from home" fallback because
 * the API has no GET-by-id endpoint that could rehydrate questions.
 *
 * Inside the bootstrap-success path the page owns `useExamSession()`
 * once and dispatches Ember vs DUO presentation based on `skin` +
 * viewport. The DUO chrome is rendered as a fullscreen overlay
 * (`fixed inset-0 z-30`) so it escapes the AppLayout's max-w/padding
 * without us having to reorganise the route tree.
 */

export function ExamPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? null;
  const fromState: ExamBootstrap | null =
    state?.startResponse && state.startResponse.sessionId === sessionId
      ? state.startResponse
      : null;

  const [bootstrap, setBootstrap] = useState<ExamBootstrap | null>(fromState);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(!fromState);

  useEffect(() => {
    if (!sessionId || fromState) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    authedFetch(`/sessions/${sessionId}`)
      .then(async (res) => {
        const body = await res.json().catch(() => null);
        if (cancelled) return;
        if (!res.ok || !body?.ok) {
          setError(body?.error?.message ?? `Sessie niet gevonden (${res.status}).`);
          return;
        }
        const d = body.data as ExamBootstrap & {
          finishedAt: number | null;
        };
        if (d.finishedAt) {
          setError("Deze sessie is al voltooid. Bekijk het rapport in plaats daarvan.");
          return;
        }
        setBootstrap({
          sessionId: d.sessionId,
          mode: d.mode,
          parts: d.parts,
          totalTurns: d.totalTurns,
          partialMediaFailure: d.partialMediaFailure,
          questions: d.questions,
          skin: d.skin,
          focusMode: d.focusMode,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Onbekende fout.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, fromState]);

  if (!sessionId) {
    return <NoSessionFallback message="Geen sessie-ID in de URL." />;
  }
  if (loading) {
    return (
      <div className="text-sm text-[var(--ink-faint)]" role="status" aria-live="polite">
        Sessie laden…
      </div>
    );
  }
  if (error) {
    return <NoBootstrapFallback sessionId={sessionId} message={error} />;
  }
  if (!bootstrap) {
    return <NoBootstrapFallback sessionId={sessionId} />;
  }
  return <ExamRunner sessionId={sessionId} bootstrap={bootstrap} />;
}

function ExamRunner({
  sessionId,
  bootstrap,
}: {
  sessionId: string;
  bootstrap: ExamBootstrap;
}) {
  // Photo alt-texts aren't on the public start response — pass an empty
  // map so the QuestionCard falls back to the "Foto N" default. (The
  // LXC's RSC fetched these from imagePromptsJson — exposing them via
  // the API is a separate ticket.)
  const questionImageAlts: Record<string, string[]> = {};

  const session = useExamSession({
    sessionId,
    initialQuestions: bootstrap.questions,
    questionImageAlts,
    mode: bootstrap.mode,
    skin: "ember",
    focusMode: false,
    initialCompleted: {} as Record<number, TurnResponse>,
  });
  const isDesktop = useBreakpoint("lg");
  const useDuoChrome = session.skin === "duo" && isDesktop;

  if (useDuoChrome) {
    return (
      <DuoChrome
        session={session}
        questionImageAlts={questionImageAlts}
      />
    );
  }
  return (
    <EmberChrome
      session={session}
      questionImageAlts={questionImageAlts}
    />
  );
}

/** Ember presentation. Renders inside AppLayout's main — no fixed
 *  positioning, just the Ember Header/main wrapper the parent already
 *  provides. */
function EmberChrome({
  session,
  questionImageAlts,
}: {
  session: ReturnType<typeof useExamSession>;
  questionImageAlts: Record<string, string[]>;
}) {
  const isDesktop = useBreakpoint("lg");
  const [pendingDuoToggle, setPendingDuoToggle] =
    useState<null | "to-duo" | "to-ember">(null);

  const hideFeedback = session.focusMode || session.deferFeedback;

  const composerOrFeedback = (
    <>
      {session.stage.kind === "answering" ? (
        <Composer
          key={`composer-${session.current}`}
          onSubmit={session.submit}
          onRecordingChange={session.onRecordingChange}
          showFirstTurnHint={session.isFirstUnanswered}
          timeUp={session.timeUp}
        />
      ) : null}

      {session.stage.kind === "feedback" && session.viewingError ? (
        <SubmitErrorPanel message={session.viewingError} onRetry={session.retryCurrent} />
      ) : session.stage.kind === "feedback" && !hideFeedback && session.score && session.turn ? (
        <FeedbackBubble
          key={session.stage.viewing}
          feedbackMd={session.turn.feedbackMd}
          score={session.score}
          audioUrl={session.turn.audioUrl}
          studentAudioUrl={session.turn.studentAudioUrl}
          transcript={session.turn.transcript}
        />
      ) : session.stage.kind === "feedback" && !hideFeedback && session.viewingPending ? (
        <FeedbackBubbleSkeleton />
      ) : session.stage.kind === "feedback" && hideFeedback ? (
        <CompactSubmitStatus pending={session.viewingPending} graded={!!session.turn} />
      ) : null}

      <ContextualPill
        isLastQuestion={session.isLastQuestion}
        prevIdx={session.prevIdx}
        nextIdx={session.nextIdx}
        showRetry={session.stage.kind === "feedback"}
        onRetry={session.retryCurrent}
        onFinalize={session.finalize}
        onJump={session.jumpToIndex}
      />
    </>
  );

  const timerCluster = (
    <>
      <TimerPill
        key={`timer-${session.current}-${session.recordingAttempt}`}
        total={TURN_SECONDS}
        active={session.stage.kind === "answering" && session.timerRunning}
        onExpire={() => {}}
      />
      {session.mode === "full" ? (
        <OverallTimer
          sessionId={session.sessionId}
          totalMinutes={FULL_EXAM_MINUTES}
          active={session.stage.kind === "answering"}
          onExpire={session.onTimeUp}
          onOneMinuteWarning={session.onOneMinuteWarning}
        />
      ) : null}
      <SettingsPopover
        feedbackEnabled={!session.focusMode}
        onChange={(enabled) => session.setFocusMode(!enabled)}
        duoEnabled={session.skin === "duo"}
        duoToggleDisabled={!isDesktop}
        onRequestDuoToggle={(next) =>
          setPendingDuoToggle(next ? "to-duo" : "to-ember")
        }
      />
    </>
  );

  return (
    <div className="flex min-h-dvh flex-col bg-[var(--bg)]">
      <ExamTopBar
        progress={
          <PartProgress
            compact
            questions={session.questions}
            current={session.displayedIndex}
            isAnswered={session.isAnswered}
          />
        }
        timers={timerCluster}
        exit={<SluitenButton onRequest={session.requestExit} />}
      />

      <main
        className={
          session.oneMinuteWarning
            ? "flex-1 overflow-y-auto border-t-2 border-[var(--accent)] px-4 py-6 pb-28 sm:px-6 sm:py-9 sm:pb-9"
            : "flex-1 overflow-y-auto px-4 py-6 pb-28 sm:px-6 sm:py-9 sm:pb-9"
        }
      >
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-5 sm:gap-6">
          <div className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)] p-5 shadow-[var(--shadow)] sm:p-7">
            <QuestionCard
              question={session.question}
              imageAltTexts={session.question ? questionImageAlts[session.question.id] ?? [] : []}
              audioFailed={session.audioFailedIdx.has(session.displayedIndex)}
              onRetryAudio={session.onAudioRetry}
            />
          </div>
          {composerOrFeedback}
        </div>
      </main>

      {session.oneMinuteToast ? (
        <div
          role="status"
          aria-live="polite"
          className="fixed top-20 left-1/2 -translate-x-1/2 z-40 rounded-full border border-[var(--accent)] bg-[var(--panel)] px-4 py-2 text-sm text-[var(--ink)] shadow-[var(--shadow)]"
        >
          Nog 1 minuut. Rond uw laatste antwoord af.
        </div>
      ) : null}
      {session.timeUp ? <TimeUpOverlay /> : null}
      <ExitConfirmDialog
        open={session.exitOpen}
        completedCount={Object.keys(session.completed).length}
        totalCount={session.questions.length}
        onCancel={session.cancelExit}
        onConfirm={session.confirmExit}
      />
      <StartDuoConfirm
        open={pendingDuoToggle === "to-duo"}
        onCancel={() => setPendingDuoToggle(null)}
        onConfirm={() => {
          setPendingDuoToggle(null);
          void session.setSkin("duo");
        }}
      />
      <ExitConfirmDuo
        open={pendingDuoToggle === "to-ember"}
        onCancel={() => setPendingDuoToggle(null)}
        onConfirm={() => {
          setPendingDuoToggle(null);
          void session.setSkin("ember");
        }}
      />
    </div>
  );
}

/** DUO presentation. Rendered as a fullscreen `fixed inset-0 z-30`
 *  overlay so it escapes AppLayout's max-w/padding without us touching
 *  the route tree. The data-skin="duo" attribute scopes the DUO CSS
 *  variables. */
function DuoChrome({
  session,
  questionImageAlts,
}: {
  session: ReturnType<typeof useExamSession>;
  questionImageAlts: Record<string, string[]>;
}) {
  const altsForCurrent = session.question
    ? questionImageAlts[session.question.id] ?? []
    : [];
  const [overzichtOpen, setOverzichtOpen] = useState(false);
  const [pendingDuoToggle, setPendingDuoToggle] =
    useState<null | "to-duo" | "to-ember">(null);

  // Lock body scroll while DUO is mounted — the overlay paints over the
  // AppLayout but we don't want the scroll bar showing the hidden chrome
  // behind it.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  return (
    <div
      data-skin="duo"
      className="fixed inset-0 z-30 flex flex-col overflow-y-auto bg-[var(--duo-bg)] text-[var(--duo-text)]"
    >
      <DuoHeader
        sessionId={session.sessionId}
        examLabel="Spreken A2 oefenexamen 1"
        totalMinutes={FULL_EXAM_MINUTES}
        active={session.stage.kind === "answering"}
        onExpire={session.onTimeUp}
        onWarn={session.onFifteenMinWarning}
        cogSlot={
          <SettingsPopover
            feedbackEnabled={!session.focusMode}
            onChange={(enabled) => session.setFocusMode(!enabled)}
            duoEnabled={session.skin === "duo"}
            duoToggleDisabled={false}
            onRequestDuoToggle={(next) =>
              setPendingDuoToggle(next ? "to-duo" : "to-ember")
            }
          />
        }
      />
      <main className="flex flex-1 flex-col items-center gap-8 px-12 pb-32 pt-10">
        <DuoQuestionCard
          question={session.question}
          imageAltTexts={altsForCurrent}
          audioFailed={session.audioFailedIdx.has(session.displayedIndex)}
          onRetryAudio={session.onAudioRetry}
        />
        <DuoRecordPanel session={session} />
      </main>
      <DuoBottomBar
        session={session}
        onOpenOverzicht={() => setOverzichtOpen(true)}
        isBookmarked={session.bookmarks.has(session.displayedIndex)}
        onToggleBookmark={() => session.toggleBookmark(session.displayedIndex)}
      />
      <OverzichtModal
        open={overzichtOpen}
        onClose={() => setOverzichtOpen(false)}
        session={session}
        bookmarks={session.bookmarks}
      />
      <TimeWarningModal
        open={session.fifteenMinWarningOpen}
        onClose={session.dismissFifteenMinWarning}
        sessionId={session.sessionId}
        totalMinutes={FULL_EXAM_MINUTES}
      />
      <StartDuoConfirm
        open={pendingDuoToggle === "to-duo"}
        onCancel={() => setPendingDuoToggle(null)}
        onConfirm={() => {
          setPendingDuoToggle(null);
          void session.setSkin("duo");
        }}
      />
      <ExitConfirmDuo
        open={pendingDuoToggle === "to-ember"}
        onCancel={() => setPendingDuoToggle(null)}
        onConfirm={() => {
          setPendingDuoToggle(null);
          void session.setSkin("ember");
        }}
      />
    </div>
  );
}

function CompactSubmitStatus({ pending, graded }: { pending: boolean; graded: boolean }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="rounded-md border border-[var(--border)] bg-[var(--panel-soft)] px-4 py-3 text-sm text-[var(--ink-muted)]"
    >
      {pending
        ? "Antwoord verstuurd. Beoordeling gebeurt op de achtergrond — u ziet alles in het rapport."
        : graded
          ? "Antwoord verstuurd. Bekijk het rapport voor de feedback."
          : "Antwoord verstuurd."}
    </div>
  );
}

function SubmitErrorPanel({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="rounded-xl border border-[var(--warn)]/40 bg-[var(--panel)] p-5 space-y-3"
    >
      <p
        className="font-mono-badge text-xs uppercase tracking-wider text-[var(--warn)]"
        aria-hidden
      >
        Beoordeling mislukt
      </p>
      <p className="text-sm leading-relaxed text-[var(--ink)]">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="text-sm text-[var(--accent)] underline underline-offset-2 hover:text-[var(--accent-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2"
      >
        ↻ Opnieuw opnemen
      </button>
    </div>
  );
}

function NoBootstrapFallback({
  sessionId,
  message,
}: {
  sessionId: string;
  message?: string;
}) {
  return (
    <div className="space-y-6 max-w-xl">
      <p className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
        Examen — sessie {sessionId}
      </p>
      <h1 className="text-2xl font-semibold tracking-tight">
        {message ?? "Sessie kon niet geladen worden."}
      </h1>
      <p className="text-sm text-[var(--ink-muted)] font-serif-italic">
        Begin een nieuwe oefening vanaf Home, of probeer de URL opnieuw te openen.
      </p>
      <Link
        to="/"
        className="inline-flex rounded-md bg-[var(--accent)] px-4 py-2 text-sm font-medium text-[var(--bg)] hover:bg-[var(--accent-hover)]"
      >
        ← Terug naar home
      </Link>
    </div>
  );
}

function NoSessionFallback({ message }: { message: string }) {
  return (
    <div className="space-y-4 max-w-xl">
      <p className="text-sm text-[var(--warn)]">{message}</p>
      <Link to="/" className="text-sm text-[var(--ink-muted)] hover:text-[var(--ink)]">
        ← Terug naar home
      </Link>
    </div>
  );
}
